<script setup lang="ts">
import { IonLabel, IonPage, IonItem, IonContent, IonIcon } from "@ionic/vue";
import {
  createOutline,
  personCircleOutline,
  calendarOutline,
  qrCodeOutline,
} from "ionicons/icons";

import { useContextStore } from "@/stores/context";
import EventSidebar from "@/views/_partials/EventSidebar.vue";
import SettingDetails from "@/views/Event/pages/Settings/Details.vue";
import SettingDate from "@/views/Event/pages/Settings/Date.vue";
import SettingScan from "@/views/Event/pages/Settings/Scan.vue";
import ManageTeam from "./ManageTeam.vue";
import { useAppStore } from "@/stores/app";
import { useWindowWidth } from "@/composition/window";
import { computed, onMounted, ref } from "vue";

const { isMobile } = useWindowWidth();
const contextStore = useContextStore();
const appStore = useAppStore();

const settingsMenu = ref("");

onMounted(() => {
  if (!isMobile.value) {
    settingsMenu.value = "basic";
  }
});

const setMenu = (menu: string) => {
  settingsMenu.value = menu;
};

const dark_mode = computed(() => appStore.settings.theme === "dark");

</script>
<template>
  <ion-page>
    <ion-content>
      <div class="flex-desktop">
        <event-sidebar v-if="!isMobile" :event="contextStore.event" />
        <div :class="dark_mode ? 'full-width' : 'colored-bg full-width'">
          <section v-if="isMobile && !settingsMenu">
            <ion-item @click="setMenu('basic')">
              <ion-label>
                <h1>Basic information</h1>
                <p>Be descriptive and name your event.</p>
              </ion-label>
              <ion-icon class="mngr_icon" :icon="createOutline"></ion-icon>
            </ion-item>

            <ion-item @click="setMenu('date-time')">
              <ion-label>
                <h1>Date and time</h1>
                <p>Set when your event starts and ends.</p>
              </ion-label>
              <ion-icon class="mngr_icon" :icon="calendarOutline"></ion-icon>
            </ion-item>
            <ion-item @click="setMenu('scan')">
              <ion-label>
                <h1>Scanning options</h1>
                <p>Choose an option that fit your event</p>
              </ion-label>
              <ion-icon class="mngr_icon" :icon="qrCodeOutline"></ion-icon>
            </ion-item>
            <ion-item @click="setMenu('team')">
              <ion-label>
                <h1>Team Management</h1>
                <p>Define who can manage your event.</p>
              </ion-label>
              <ion-icon
                class="mngr_icon"
                :icon="personCircleOutline"
              ></ion-icon>
            </ion-item>
          </section>
          <section v-else-if="isMobile && settingsMenu">
            <setting-details
              v-if="settingsMenu === 'basic'"
              @cancel="setMenu('')"
            />
            <setting-date
              v-if="settingsMenu === 'date-time'"
              @cancel="setMenu('')"
            />
            <setting-scan
              v-if="settingsMenu === 'scan'"
              @cancel="setMenu('')"
            />
            <manage-team v-if="settingsMenu === 'team'" @cancel="setMenu('')" />
          </section>
          <section v-else>
            <div class="container">
              <h1>Setup</h1>
              <div class="flex-desktop" style="margin-top: 30px">
                <div class="horizontal-tabs">
                  <div
                    class="horizontal-tab"
                    :class="{ active: settingsMenu === 'basic' }"
                    @click="setMenu('basic')"
                  >
                    <span>Basic informations</span>
                  </div>
                  <div
                    class="horizontal-tab"
                    :class="{ active: settingsMenu === 'date-time' }"
                    @click="setMenu('date-time')"
                  >
                    <span>Date and time</span>
                  </div>
                  <div
                    class="horizontal-tab"
                    :class="{ active: settingsMenu === 'scan' }"
                    @click="setMenu('scan')"
                  >
                    <span>Scanning options</span>
                  </div>
                  <div
                    class="horizontal-tab"
                    :class="{ active: settingsMenu === 'team' }"
                    @click="setMenu('team')"
                  >
                    <span>Team management</span>
                  </div>
                </div>
                <div class="full-width" style="margin-right: 30px">
                  <setting-details v-if="settingsMenu === 'basic'" />
                  <setting-date v-if="settingsMenu === 'date-time'" />
                  <setting-scan v-if="settingsMenu === 'scan'" />
                  <manage-team v-if="settingsMenu === 'team'" />
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<style scoped lang="scss">
.horizontal-tabs {
  margin-right: 70px;
}

.horizontal-tab {
  width: 300px;
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.horizontal-tab.active {
  background-color: #edeff2;
  color: black !important;
}

.horizontal-tab:hover {
  color: #2dd36f;
}

@media (min-width: 768px) {
  .colored-bg.full-width {
    overflow-y: auto;
    height: calc(100vh - 72px);
  }

  .container {
    min-height: 660px;
  }
}
</style>
